
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { useAnalytics, AnalyticsEvents } from "@/hooks/useAnalytics";
import { useMetaPixel, MetaPixelEvents } from "@/hooks/useMetaPixel";
import { useLanguage } from "@/components/LanguageContext";

export const loginSchema = z.object({
  email: z.string().email("Formato de email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
});

export const registerSchema = z.object({
  firstName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  lastName: z.string().min(2, "Sobrenome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Formato de email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
  country: z.string().min(1, "País é obrigatório"),
  city: z.string().min(1, "Cidade é obrigatória"),
});

export type RegisterFormValues = z.infer<typeof registerSchema>;

export const useStudentAuth = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { sendEvent } = useAnalytics();
  const { sendEvent: sendPixelEvent } = useMetaPixel();
  const { language } = useLanguage();

  const handleLogin = async (email: string, password: string) => {
    setIsSubmitting(true);
    try {
      console.log("Tentando fazer login com:", email);
      const { data: signInData, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) throw loginError;
      
      if (!signInData.user) {
        throw new Error("Erro ao obter dados do usuário após login");
      }

      console.log("Login bem sucedido, verificando perfil do aluno...");
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_student')
        .eq('id', signInData.user.id)
        .single();

      if (profileError) {
        await supabase.auth.signOut();
        throw new Error("Erro ao verificar perfil de aluno: " + profileError.message);
      }

      if (!profile?.is_student) {
        await supabase.auth.signOut();
        throw new Error("Esta conta não é uma conta de aluno");
      }

      console.log("Login bem sucedido como aluno:", profile);
      
      sendEvent(AnalyticsEvents.USER_LOGIN, {
        event_category: 'authentication',
        event_label: 'student',
        user_type: 'student'
      });
      
      sendPixelEvent(MetaPixelEvents.CUSTOM_EVENT, {
        content_name: 'student_login',
        content_category: 'authentication'
      });
      
      toast.success("Login realizado com sucesso");
      
      let dashboardPath = "/student-dashboard";
      if (language === 'pt') dashboardPath = "/pt/student-dashboard";
      else if (language === 'es') dashboardPath = "/es/student-dashboard";
      else if (language === 'fr') dashboardPath = "/fr/student-dashboard";
      
      navigate(dashboardPath, { replace: true });
      
      return true;
    } catch (error: any) {
      console.error("Auth error:", error);
      toast.error(error.message || "Ocorreu um erro durante a autenticação");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (values: RegisterFormValues) => {
    setIsSubmitting(true);
    try {
      console.log("Criando nova conta de estudante...");
      
      // O trigger da base de dados vai automaticamente criar o perfil e registo de estudante
      // NÃO criamos registos manuais aqui - deixamos que o trigger handle_new_user() faça o trabalho
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            is_student: true,
            country: values.country,
            city: values.city
          },
        },
      });

      if (signUpError) throw signUpError;

      if (!signUpData.user) {
        throw new Error("Erro ao criar conta de usuário");
      }

      console.log("Conta criada com sucesso:", signUpData.user.id);

      sendEvent(AnalyticsEvents.USER_SIGNUP, {
        event_category: 'authentication',
        event_label: 'student',
        user_type: 'student'
      });
      
      sendPixelEvent(MetaPixelEvents.COMPLETE_REGISTRATION, {
        content_name: 'student_registration',
        content_category: 'authentication',
        status: true
      });

      toast.success(
        "Conta criada com sucesso! Verifique seu email para confirmar o cadastro."
      );
      
      let dashboardPath = "/student-dashboard";
      if (language === 'pt') dashboardPath = "/pt/student-dashboard";
      else if (language === 'es') dashboardPath = "/es/student-dashboard";
      else if (language === 'fr') dashboardPath = "/fr/student-dashboard";
      
      navigate(dashboardPath, { replace: true });
      
      return true;
    } catch (error: any) {
      console.error("Auth error:", error);
      toast.error(error.message || "Ocorreu um erro durante a autenticação");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setIsSubmitting(true);
    try {
      await supabase.auth.signOut();
      
      let homePath = "/";
      if (language === 'pt') homePath = "/pt";
      else if (language === 'es') homePath = "/es";
      else if (language === 'fr') homePath = "/fr";
      
      navigate(homePath, { replace: true });
      return true;
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message || "Ocorreu um erro durante o logout");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleLogin,
    handleRegister,
    handleLogout
  };
};
